import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './BrandProducts.modern.css';

const ModernBrandProducts = () => {
    const { brandSlug } = useParams();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortBy, setSortBy] = useState('best-selling');
    const [priceRange, setPriceRange] = useState([0, 5000]);
    // const [showFilters, setShowFilters] = useState(true);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                setLoading(true);
                // Use product-groups API with vendor filter
                const response = await fetch(`${API_URL}/product-groups?vendor=${brandSlug}&limit=10000`);
                const data = await response.json();

                if (data.success) {
                    setProducts(data.data);
                } else {
                    setError('Failed to load products');
                }
            } catch (err) {
                console.error('Error fetching products:', err);
                setError('Failed to load products');
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, [brandSlug, API_URL]);

    const getImageUrl = (localPath) => {
        if (!localPath) return null;
        if (localPath.startsWith('http')) return localPath;
        const baseUrl = API_URL.replace('/api', '');
        if (localPath.startsWith('/uploads/')) return `${baseUrl}${localPath}`;
        return `${baseUrl}/api/images/${localPath}`;
    };

    const getBrandName = (slug) => {
        const brandNames = {
            'opi': 'OPI',
            'pla': 'PLA',
            'niko': 'NIKO',
            'dnd': 'DND',
            'la-palm': 'LA PALM',
            'cre8tion': 'Cre8tion',
            'ibd': 'IBD',
            'nghia': 'NGHIA',
            'nexgen': 'Nexgen',
            'kupa': 'Kupa',
            'led-lamps': 'LED Lamps',
            'vichy-spa': 'Vichy Spa',
            'md-creative': 'MD Creative Designs',
            'sns': 'SNS',
        };
        return brandNames[slug] || slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    };

    const getSortedProducts = () => {
        let sorted = [...products];
        
        // Filter by price range (use min_price for product groups)
        sorted = sorted.filter(p => {
            const price = parseFloat(p.min_price || 0);
            return price >= priceRange[0] && price <= priceRange[1];
        });
        
        // Sort
        switch(sortBy) {
            case 'price-low':
                sorted.sort((a, b) => parseFloat(a.min_price || 0) - parseFloat(b.min_price || 0));
                break;
            case 'price-high':
                sorted.sort((a, b) => parseFloat(b.min_price || 0) - parseFloat(a.min_price || 0));
                break;
            case 'name-az':
                sorted.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
                break;
            default:
                break;
        }

        return sorted;
    };

    const clearFilters = () => {
        setPriceRange([0, 200]);
    };

    const hasActiveFilters = priceRange[0] > 0 || priceRange[1] < 200;

    const filteredProducts = getSortedProducts();

    const handleQuickAdd = (e, product) => {
        e.stopPropagation(); // Prevent navigation to product-page
        navigate(`/product-group/${product.group_id}`);
        return;
    };

    if (loading) {
        return (
            <div className="modern-collection-page">
                <div className="collection-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading products...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="modern-collection-page">
                <div className="collection-error">{error}</div>
            </div>
        );
    }

    return (
        <div className="modern-collection-page">
            {/* Breadcrumbs */}
            <div className="breadcrumbs">
                <span onClick={() => navigate('/')} className="breadcrumb-link">Home</span>
                <span className="breadcrumb-separator">/</span>
                <span onClick={() => navigate('/products')} className="breadcrumb-link">Brands</span>
                <span className="breadcrumb-separator">/</span>
                <span className="breadcrumb-current">{getBrandName(brandSlug)}</span>
            </div>

            {/* Collection Header */}
            <div className="collection-header">
                <div className="collection-title-section">
                    <h1 className="collection-title">{getBrandName(brandSlug)} Products</h1>
                    <p className="collection-count">{filteredProducts.length} items</p>
                </div>
                <div className="collection-sort">
                    <select 
                        value={sortBy} 
                        onChange={(e) => setSortBy(e.target.value)}
                        className="sort-select"
                    >
                        <option value="best-selling">Best Selling</option>
                        <option value="price-low">Price: Low to High</option>
                        <option value="price-high">Price: High to Low</option>
                        <option value="name-az">Name: A-Z</option>
                    </select>
                </div>
            </div>

            {/* Active Filters */}
            {hasActiveFilters && (
                <div className="active-filters">
                    <span className="active-filters-label">Active filters:</span>
                    {(priceRange[0] > 0 || priceRange[1] < 200) && (
                        <span className="filter-chip">
                            ${priceRange[0]} - ${priceRange[1]}
                            <button onClick={() => setPriceRange([0, 200])}>×</button>
                        </span>
                    )}
                    <button className="clear-all-btn" onClick={clearFilters}>
                        Clear all
                    </button>
                </div>
            )}

            {/* Content with Sidebar */}
            <div className="collection-content-with-sidebar">
                {/* Left Sidebar Filters */}
                <aside className="filters-sidebar">
                    <div className="filters-sidebar-header">
                        <h3>Filters</h3>
                        {hasActiveFilters && (
                            <button className="clear-all-sidebar-btn" onClick={clearFilters}>
                                Clear all
                            </button>
                        )}
                    </div>

                    {/* Price Range Filter */}
                    <div className="filter-section">
                        <h4 className="filter-section-title">Price Range</h4>
                        <div className="price-range-inputs">
                            <input
                                type="number"
                                min="0"
                                max="200"
                                value={priceRange[0]}
                                onChange={(e) => setPriceRange([parseInt(e.target.value) || 0, priceRange[1]])}
                                className="price-input"
                                placeholder="Min"
                            />
                            <span className="price-separator">to</span>
                            <input
                                type="number"
                                min="0"
                                max="200"
                                value={priceRange[1]}
                                onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value) || 200])}
                                className="price-input"
                                placeholder="Max"
                            />
                        </div>
                    </div>
                </aside>

                {/* Main Products Grid */}
                <main className="products-main-content">
                    <div className="collection-grid">
                        {filteredProducts.map((product) => (
                            <div
                                key={product.group_id}
                                className="modern-product-card"
                                onClick={() => navigate(`/product-group/${product.group_id}`)}
                            >
                                <div className="product-image-wrapper">
                                    {product.hero_image || (product.preview_variants && product.preview_variants[0]?.image_url) ? (
                                        <img
                                            src={product.hero_image ? getImageUrl(product.hero_image) : product.preview_variants[0].image_url}
                                            alt={product.title}
                                            className="product-image"
                                            onError={(e) => {
                                                e.target.src = '/images/placeholder.webp';
                                            }}
                                        />
                                    ) : (
                                        <div className="no-image-placeholder">No Image</div>
                                    )}
                                    <button 
                                        className="quick-add-button"
                                        onClick={(e) => handleQuickAdd(e, product)}
                                    >
                                        {product.total_variants > 1 ? 'Select Options' : 'Select Options'}
                                    </button>
                                </div>
                                <div className="product-card-info">
                                    <p className="product-brand">{getBrandName(brandSlug)}</p>
                                    <h3 className="product-card-name">{product.title}</h3>
                                    <p className="product-category">{product.category_name || product.department_name || 'Uncategorized'}</p>
                                    <div className="product-card-footer">
                                        <p className="product-card-price">
                                            ${product.min_price ? Number(product.min_price).toFixed(2) : '0.00'}
                                            {product.max_price && product.max_price !== product.min_price && (
                                                <span> - ${Number(product.max_price).toFixed(2)}</span>
                                            )}
                                        </p>
                                        {product.in_stock && (
                                            <span className="stock-indicator">
                                                <span className="stock-dot"></span>
                                                In stock
                                            </span>
                                        )}
                                    </div>
                                    {product.total_variants > 1 && (
                                        <p className="variant-count">{product.total_variants} options</p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {filteredProducts.length === 0 && (
                        <div className="no-products-message">
                            <p>No products found matching your criteria</p>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default ModernBrandProducts;
